import { Button, Chip, Container, Divider, TextField, Typography, TypographyVariant } from '@cleartrip/ct-design-components'
import React, { useEffect, useState } from 'react'

import { DownChevronNew } from '@/assets/icons/DownChevron'
import { Dropdown, DropdownMenu } from '@/components/oldComponents/Dropdown'
import TravellersCounter from '@/components/srp/ModifySearch/Travellers/TravellersCounter'
import { travellersList } from '@/constants/components/common'
import { PE_SRP_GLASS_FIELD_CSS } from '@/constants/flights/premiumEconomy'
import { TRAVELLERS } from '@/constants/Srp'
import usePremiumEconomyExperiment from '@/hooks/flights/usePremiumEconomyExperiment'
import { pluralize } from '@/utils/general/browserHelper'

const ECONOMY_PE_COMBINED_LABEL = 'Economy/Premium Economy'

const Travellers = ({ query, openDropDown, closeDropdown = () => {}, setQuery, isOpen, activeFare = '', isThemed = false, isIntl = false, fieldWidth = 220, gapClassName = 'ml-3', textVariant = '', fieldCss = {}, iconSize = 22 }) => {
    const initialTravellers = {
        adults: parseInt(query.adults, 10),
        childs: parseInt(query.childs, 10),
        infants: parseInt(query.infants, 10)
    }
    const [travellers, setTravellers] = useState(initialTravellers)
    const { adults, childs, infants } = travellers
    const travellerGroup1 = { adults, childs }
    const travellerGroup2 = { infants }
    const totalValueGroup1 = Object.keys(travellerGroup1).reduce((sum, key) => sum + travellerGroup1[key], 0)
    const totalValueGroup2 = Object.keys(travellerGroup2).reduce((sum, key) => sum + travellerGroup2[key], 0)
    const [total, setTotal] = useState(totalValueGroup1 + totalValueGroup2)
    const [isDropdownOpen, setIsDropdownOpen] = useState(isOpen)
    // Feed the form's live route so the pill reacts when the user switches dom <-> intl.
    const { isPeTestVariant } = usePremiumEconomyExperiment(isIntl)
    // Combined pill is test-only; control keeps plain "Economy".
    const economyLabel = isPeTestVariant ? ECONOMY_PE_COMBINED_LABEL : 'Economy'
    
    useEffect(() => {
        setIsDropdownOpen(isOpen)
    }, [isOpen])

    const selectedClassName: Record<any, any> = {
        Economy: economyLabel,
        ['Premium Economy']: 'Premium economy',
        Business: 'Business class',
        First: 'First class'
    }
    const selectedClassNameRecord: Record<string, string> = {
        Economy: 'Economy',
        [ECONOMY_PE_COMBINED_LABEL]: 'Economy',
        ['Business class']: 'Business',
        ['First class']: 'First',
        ['Premium economy']: 'Premium Economy'
    }
    
    const initialClass = selectedClassName[query.class] || economyLabel
    const [selectedClass, setSelectedClass] = useState(initialClass)
    const classDisplayName: Record<string, string> = {
        'Economy': economyLabel,
        [ECONOMY_PE_COMBINED_LABEL]: economyLabel,
        'Business class': 'Business',
        'First class': 'First class',
        'Premium economy': 'Premium'
    }
    const hasChanges = 
        travellers.adults !== parseInt(query.adults, 10) ||
        travellers.childs !== parseInt(query.childs, 10) ||
        travellers.infants !== parseInt(query.infants, 10) ||
        selectedClass !== initialClass

    function travellersUpdate() {
        const newTotal = travellers.adults + travellers.childs + travellers.infants
        setTotal(newTotal)
        setQuery({
            ...query,
            adults: travellers.adults.toString(),
            childs: travellers.childs.toString(),
            infants: travellers.infants.toString(),
            class: selectedClassNameRecord[selectedClass]
        })
        setIsDropdownOpen(false)
        closeDropdown()
    }

    function setClass(selectedclass: string) {
        setSelectedClass(selectedclass)
    }

    const onTravellersClick = () => {
        if (isDropdownOpen) {
            setIsDropdownOpen(false)
            closeDropdown()
        } else {
            openDropDown(TRAVELLERS)
            setIsDropdownOpen(true)
        }
    }
    
    const handleOutsideClick = () => {
        setIsDropdownOpen(false)
        closeDropdown()
    }
    
    return (
        <Container className={`${gapClassName} p-relative`}>
            <Dropdown onOutsideClick={handleOutsideClick}>
                <TextField
                    height={40}
                    name="travellers"
                    select={true}
                    styleConfig={{
                        root: {
                            css: {
                                borderRadius: '6px',
                                padding: '10px 4px 10px 12px',
                                ...(isThemed ? PE_SRP_GLASS_FIELD_CSS : {}),
                                ...fieldCss,
                                '& input': { textOverflow: 'ellipsis' },
                            }
                        }
                    }}
                    suffix={
                        <Container height={`${iconSize}px`} style={{ rotate: isDropdownOpen ? '-180deg' : '0deg' }} width={`${iconSize}px`}>
                            <DownChevronNew height={iconSize} width={iconSize} />
                        </Container>
                    }
                    textTypographyProps={textVariant ? { variant: textVariant as TypographyVariant } : undefined}
                    value={`${pluralize(total, 'Traveller')}, ${classDisplayName[selectedClass]}`}
                    width={fieldWidth}
                    onClick={onTravellersClick}
                />
                <DropdownMenu className="br-16" distance={10} open={isDropdownOpen} position="right" width={400}>
                    <Container className="pt-4 pb-2">
                        <Container className="px-6 py-1">
                            <Typography className="fw-500" color="subheading" variant="HM4">
                                Travellers
                            </Typography>
                        </Container>
                        {travellersList.map((item, index) => {
                            return (
                                <TravellersCounter
                                    key={index}
                                    activeFare={activeFare}
                                    query={query}
                                    setTravellers={setTravellers}
                                    travellerData={item}
                                    travellers={travellers}
                                />
                            )
                        })}
                    </Container>
                    <Container className="py-2">
                        <Divider />
                    </Container>
                    <Container className="px-6 pt-3 pb-1">
                        <Typography className="fw-500" color="subheading" variant="HM4">
                            Class
                        </Typography>
                    </Container>
                    <Container className="px-6 py-3">
                        <Container className="flex flex-wrap" style={{ gap: '12px' }}>
                            {Object.values(selectedClassName).map((item, index) => {
                                return (
                                    <Chip
                                        key={index}
                                        isSelected={selectedClass === item}
                                        label={item}
                                        styleConfig={{
                                            labelTypography: {
                                                className: 'c-pointer fw-400'
                                            },
                                            root: {
                                                className: 'c-pointer'
                                            }
                                        }}
                                        onClick={() => {
                                            setClass(item)
                                        }}
                                    />
                                )
                            })}
                        </Container>
                    </Container>
                    <Container className="px-6 pb-6">
                        <Button
                            color="primary"
                            disabled={!hasChanges}
                            isFullWidth={true}
                            size="medium"
                            variant="contained"
                            onClick={travellersUpdate}
                        >
                            Update
                        </Button>
                    </Container>
                </DropdownMenu>
            </Dropdown>
        </Container>
    )
}

export default Travellers
